/**
 * Setup task #1 — the client accepts the declarations once, before any form.
 *
 * Deliberately NOT pre-ticked and NOT skippable: this record is what ADI relies on when lodging,
 * so the client has to take a positive action. One acceptance covers every form on the account,
 * now and in future.
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMatter } from '../hooks/useMatter';
import { useDeclarations } from '../hooks/useDeclarations';
import { declarationAudienceFor } from '../lib/declarations/audience';
import { getDeclarationSet } from '../lib/declarations/declarationSet';

export default function DeclarationsSetup() {
  // Must mirror DeclarationsGate EXACTLY, or someone exempt could still land here and record an
  // acceptance they were never meant to make. `actuallyStaff` (not isStaff) so an impersonating
  // staff member can never accept on a client's behalf.
  const { contact, activeMatter, actuallyStaff, loading } = useMatter();
  const audience = declarationAudienceFor(contact?.contact_type, activeMatter?.visa_subclass);
  const exempt = actuallyStaff || audience === null;
  const { accept } = useDeclarations(contact?.id ?? null, exempt, audience);

  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Someone exempt who navigates here directly gets nothing to sign. Bounce them rather than render
  // a set they do not make; the alternative is an accept button that writes a false attestation.
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <div className="text-sm text-slate-500">Loading...</div>
      </div>
    );
  }
  if (exempt || !audience) return <Navigate to="/dashboard" replace />;

  const set = getDeclarationSet(audience);

  const onAccept = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await accept();
      // The gate in AuthGuard re-reads acceptance and will let them through.
      window.location.assign('/dashboard');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your acceptance. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <p className="text-xs font-semibold uppercase tracking-wide text-[#2CABEA]">Step 1 of 1</p>
      <h1 className="text-2xl font-semibold text-[#0B2341] mt-1">{set.heading}</h1>
      <p className="text-slate-600 mt-2">{set.intro}</p>

      <ul className="mt-6 space-y-3">
        {set.items.map((d) => (
          <li key={d.id} className="flex gap-3 rounded-lg border border-slate-200 bg-white p-3.5">
            <span aria-hidden="true" className="text-[#2CABEA] mt-0.5">
              &#10003;
            </span>
            <span className="text-slate-700 text-[0.95rem] leading-relaxed">{d.text}</span>
          </li>
        ))}
      </ul>

      <label className="flex items-start gap-3 mt-7 cursor-pointer rounded-lg border border-slate-300 p-3.5 hover:bg-slate-50">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="mt-1"
        />
        <span className="text-slate-800">{set.confirmLabel}</span>
      </label>

      {error && (
        <p role="alert" className="mt-4 text-sm text-red-600">
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={onAccept}
        disabled={!confirmed || submitting}
        className="mt-5 w-full rounded-lg bg-[#0B2341] px-4 py-3 text-white font-medium disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {submitting ? 'Saving...' : 'Confirm and continue'}
      </button>

      <p className="mt-4 text-xs text-slate-500">
        We record the date you confirmed this, and the exact wording you agreed to, so it can be
        shown to the Department if it is ever needed. If the wording changes, we will ask you again.
      </p>
    </div>
  );
}
